"use client";

// Recent tracked sessions from this device's IndexedDB. Local-first: shows
// sessions belonging to the current account, with each card linking to
// its Trip Story. Renders nothing while empty so the companion page stays
// clean for first-time visitors.

import { useEffect, useState } from "react";
import Link from "next/link";
import { listSessions, type SessionRecord } from "@/lib/companion/store";
import { companionSessionCapture, captureLabel } from "@/lib/companion/session-provenance";
import { buildReportCardSummaryLine } from "@/lib/companion/report-card";
import { RecordingAccountBoundary } from "./RecordingAccountBoundary";

const SPORT_ICON: Record<SessionRecord["sport"], string> = {
  ski: "⛷️",
  mtb: "🚵",
  surf: "🌊",
  coastal: "🌊",
};

function dayLabel(t: number): string {
  return new Date(t).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}

export function RecentSessions() {
  return <RecordingAccountBoundary quiet><OwnedRecentSessions /></RecordingAccountBoundary>;
}
function OwnedRecentSessions() {
  const [sessions, setSessions] = useState<SessionRecord[]>([]);

  useEffect(() => {
    let cancelled = false;
    listSessions()
      .then((s) => { if (!cancelled) setSessions(s.slice(0, 6)); })
      .catch(() => { if (!cancelled) setSessions([]); });
    return () => { cancelled = true; };
  }, []);

  if (sessions.length === 0) return null;

  return (
    <section className="mt-10">
      <h2 className="font-serif text-[22px] font-bold text-navy">Your sessions</h2>
      <p className="mt-1 text-[13px] text-text-muted">Stored on this device. Tap one for its story.</p>
      <ul className="mt-4 space-y-2">
        {sessions.map((s) => (
          <li key={s.id}>
            <Link
              href={`/companion/story/${encodeURIComponent(s.id)}`}
              className="flex items-center gap-3 rounded-[14px] border border-border-light bg-white px-4 py-3 no-underline transition-colors hover:border-gold/40"
            >
              <span aria-hidden className="text-[20px]">{SPORT_ICON[s.sport]}</span>
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] font-semibold text-navy">
                  {s.placeLabel ?? "Session"} · {dayLabel(s.startedAt)}
                </span>
                <span className="block text-[12.5px] text-text-muted">
                  {companionSessionCapture(s.consentVersion) === "tracked" ? buildReportCardSummaryLine(s) : captureLabel(companionSessionCapture(s.consentVersion))}
                </span>
              </span>
              <span aria-hidden className="text-text-muted">&rarr;</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
